
import { format } from "date-fns";
import { pt, enUS } from "date-fns/locale";
import { User, UserCog, Trash2 } from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { useNavigate } from "react-router-dom";
import { useLanguage } from "@/components/LanguageContext";
import { useLanguagePath } from "@/utils/language";
import { useTranslations } from "@/hooks/useTranslations";

interface SessionsTableProps {
  sessions: any[];
  onDeleteSession: (id: string) => void;
}

export const SessionsTable = ({ sessions, onDeleteSession }: SessionsTableProps) => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { getPath } = useLanguagePath();
  const { t } = useTranslations("training-sessions");
  
  const dateLocale = language === 'pt' ? pt : enUS;
  
  // Verifica se o personal pode apagar a sessão (apenas as criadas por ele)
  const canDeleteSession = (session: any) => {
    return session.created_by_type === 'trainer';
  };
  
  if (!sessions?.length) {
    return (
      <div className="text-center py-4 bg-white/80 dark:bg-gray-800/80 rounded-xl shadow-sm border border-slate-100/50 dark:border-gray-700/50 backdrop-blur-sm">
        <p className="text-muted-foreground dark:text-gray-300">{t("table.noSessionsFound")}</p>
      </div>
    );
  }
  
  return (
    <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-lg rounded-xl shadow-md border border-slate-100/50 dark:border-gray-700/50 overflow-hidden hover:shadow-lg transition-all">
      <div className="overflow-x-auto">
        <Table>
          <TableHeader className="bg-slate-50/50 dark:bg-gray-800/70">
            <TableRow>
              <TableHead className="dark:text-gray-300">{t("table.date")}</TableHead>
              <TableHead className="dark:text-gray-300">{t("table.student")}</TableHead>
              <TableHead className="dark:text-gray-300">{t("table.workout")}</TableHead>
              <TableHead className="dark:text-gray-300">{t("table.day")}</TableHead>
              <TableHead className="dark:text-gray-300">{t("table.status")}</TableHead>
              <TableHead className="dark:text-gray-300">{t("table.recordedBy")}</TableHead>
              <TableHead className="w-[100px] dark:text-gray-300">{t("table.actions")}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {sessions.map((session) => (
              <TableRow 
                key={session.id}
                className="cursor-pointer hover:bg-slate-50/50 dark:hover:bg-gray-700/50"
              >
                <TableCell onClick={() => navigate(getPath(`/dashboard/training-sessions/${session.id}`))}>
                  {format(new Date(session.created_at), "PPp", { locale: dateLocale })}
                </TableCell>
                <TableCell onClick={() => navigate(getPath(`/dashboard/training-sessions/${session.id}`))}>
                  {session.student?.first_name} {session.student?.last_name}
                </TableCell>
                <TableCell onClick={() => navigate(getPath(`/dashboard/training-sessions/${session.id}`))}>
                  {session.workout?.title}
                </TableCell>
                <TableCell onClick={() => navigate(getPath(`/dashboard/training-sessions/${session.id}`))}>
                  {t("table.day_prefix")} {session.workout_day}
                </TableCell>
                <TableCell onClick={() => navigate(getPath(`/dashboard/training-sessions/${session.id}`))}>
                  {session.status}
                </TableCell>
                <TableCell>
                  <TooltipProvider>
                    <Tooltip>
                      <TooltipTrigger>
                        <Badge 
                          variant={session.created_by_type === 'student' ? "warning" : "default"}
                          className="cursor-help"
                        >
                          {session.created_by_type === 'student' ? (
                            <User className="h-3 w-3 mr-1" />
                          ) : (
                            <UserCog className="h-3 w-3 mr-1" />
                          )}
                          {session.created_by_type === 'student' ? t("table.student") : t("table.personal")}
                        </Badge>
                      </TooltipTrigger>
                      <TooltipContent className="dropdown-opaque">
                        <p>
                          {session.created_by_type === 'student'
                            ? t("table.sessionRecordedByStudent")
                            : `${t("table.sessionRecordedByPersonal")} ${session.trainer?.first_name} ${session.trainer?.last_name}`}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </TableCell>
                <TableCell>
                  {canDeleteSession(session) && (
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={(e) => {
                        e.stopPropagation();
                        onDeleteSession(session.id);
                      }}
                      className="hover:bg-destructive/10 hover:text-destructive dark:text-gray-300 dark:hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                      <span className="sr-only">{t("button.delete")}</span>
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
};
